/**
 * Icon Cache Utility
 * 
 * Implements multi-layer caching strategy:
 * 1. Browser Cache API - Persists across page navigations, survives page reloads
 * 2. Image preloading - Leverages browser's HTTP cache
 * 3. URL tracking - Prevents duplicate requests
 * 
 * Best Practices:
 * - Cache API stores actual image responses (survives navigation)
 * - Browser HTTP cache handles preloaded images
 * - sessionStorage tracks which URLs we've seen (quick lookup)
 */

const CACHE_NAME = 'icon-cache-v1'
const URL_TRACKING_KEY = 'icon_urls_tracked'

// Track which URLs we've requested (prevents duplicate requests)
const requestedUrls = new Set()

// Initialize: Load tracked URLs from sessionStorage
if (typeof window !== 'undefined' && window.sessionStorage) {
  try {
    const tracked = sessionStorage.getItem(URL_TRACKING_KEY)
    if (tracked) {
      const urls = JSON.parse(tracked)
      urls.forEach((url) => requestedUrls.add(url))
    }
  } catch (e) {
    console.debug('Failed to load tracked URLs from sessionStorage:', e)
  }
}

/**
 * Save tracked URLs to sessionStorage
 * Handles quota exceeded errors gracefully
 */
const saveTrackedUrls = () => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const urls = Array.from(requestedUrls)
      sessionStorage.setItem(URL_TRACKING_KEY, JSON.stringify(urls))
    } catch (e) {
      // Handle quota exceeded - clear old entries if storage is full
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        try {
          // Try saving only recent URLs (keep last 500)
          const recentUrls = Array.from(requestedUrls).slice(-500)
          sessionStorage.setItem(URL_TRACKING_KEY, JSON.stringify(recentUrls))
          // Update in-memory set to match
          requestedUrls.clear()
          recentUrls.forEach((url) => requestedUrls.add(url))
        } catch (e2) {
          // If still fails, clear tracking (cache will still work via Cache API)
          console.debug('SessionStorage quota exceeded, clearing tracking:', e2)
          sessionStorage.removeItem(URL_TRACKING_KEY)
        }
      } else {
        console.debug('Failed to save tracked URLs to sessionStorage:', e)
      }
    }
  }
}

/**
 * Get or create the Cache API cache
 */
const getCache = async () => {
  if (typeof window === 'undefined' || !('caches' in window)) {
    return null
  }

  try {
    return await caches.open(CACHE_NAME)
  } catch (e) {
    console.debug('Cache API not available:', e)
    return null
  }
}

/**
 * Check if image is already cached in Cache API
 */
const isImageCached = async (url) => {
  const cache = await getCache()
  if (!cache) return false

  try {
    const cached = await cache.match(url)
    return !!cached
  } catch (e) {
    return false
  }
}

/**
 * True when the URL is from another origin (e.g. S3, CDN).
 * For cross-origin URLs we skip fetch() to avoid CORS errors when the server
 * doesn't allow our origin (e.g. S3 bucket without CORS). Preload via <img> only.
 */
const isCrossOrigin = (url) => {
  if (typeof window === 'undefined' || !url || !url.startsWith('http')) return false
  try {
    const u = new URL(url)
    return u.origin !== window.location.origin
  } catch {
    return false
  }
}

/**
 * Cache an image URL using Cache API
 * This persists across page navigations!
 * Cross-origin URLs (e.g. merklebase-assets.s3.amazonaws.com) skip fetch to avoid
 * CORS errors; use preload only. To fix CORS, add CORS rules on the bucket.
 */
const cacheImage = async (url) => {
  // Skip if already requested (prevents duplicate network requests)
  if (requestedUrls.has(url)) {
    return
  }

  // Check if already cached
  if (await isImageCached(url)) {
    requestedUrls.add(url)
    saveTrackedUrls()
    return
  }

  // Mark as requested immediately to prevent concurrent requests
  requestedUrls.add(url)
  saveTrackedUrls()

  const cache = await getCache()
  if (!cache) {
    // Fallback: Just preload the image (browser HTTP cache will handle it)
    preloadImage(url)
    return
  }

  // Cross-origin URLs: skip fetch() to avoid CORS when server doesn't allow our origin (e.g. S3)
  if (isCrossOrigin(url)) {
    preloadImage(url)
    return
  }

  try {
    // Fetch and cache the image (same-origin only)
    const response = await fetch(url, {
      mode: 'cors',
      cache: 'default', // Use browser's HTTP cache first
    })

    if (response.ok) {
      // Clone response because response body can only be consumed once
      const responseClone = response.clone()
      
      // Store in Cache API (persists across navigations)
      await cache.put(url, responseClone)
    }
  } catch (e) {
    console.debug(`Failed to cache image ${url}:`, e)
    // Even if caching fails, preload the image so browser cache handles it
    preloadImage(url)
  }
}

/**
 * Preload an image (leverages browser's HTTP cache)
 * This is a fallback when Cache API is not available
 */
const preloadImage = (url) => {
  if (typeof window === 'undefined') return

  try {
    const img = new Image()
    img.src = url
    // Don't need to wait for load - browser will cache it
  } catch (e) {
    console.debug(`Failed to preload image ${url}:`, e)
  }
}

/**
 * Get cached image URL
 * Returns the original URL - browser will automatically use cached version
 * from Cache API or HTTP cache
 */
const getCachedImageUrl = async (url) => {
  if (!url) return null

  // Check Cache API first
  const cache = await getCache()
  if (cache) {
    try {
      const cached = await cache.match(url)
      if (cached) {
        // Return original URL - browser will use cached version
        return url
      }
    } catch (e) {
      console.debug(`Failed to get cached image ${url}:`, e)
    }
  }

  // If not in Cache API, check if we've requested it before
  // Browser's HTTP cache should handle it
  if (requestedUrls.has(url)) {
    return url // Browser cache should have it
  }

  return null
}

/**
 * Cache multiple images in parallel
 */
const cacheImages = async (urls) => {
  if (!urls || urls.length === 0) return

  // Filter out already requested URLs
  const urlsToCache = urls.filter((url) => url && !requestedUrls.has(url))

  if (urlsToCache.length === 0) return

  // Cache in parallel (but limit concurrent requests)
  const BATCH_SIZE = 10
  for (let i = 0; i < urlsToCache.length; i += BATCH_SIZE) {
    const batch = urlsToCache.slice(i, i + BATCH_SIZE)
    await Promise.allSettled(batch.map((url) => cacheImage(url)))
  }
}

/**
 * Preload common icons on app initialization
 */
const preloadCommonIcons = async (iconUrls) => {
  if (!iconUrls || iconUrls.length === 0) return

  // Cache common icons
  await cacheImages(iconUrls.slice(0, 50)) // Limit to first 50 to avoid overwhelming
}

/**
 * Clear the icon cache
 */
const clearCache = async () => {
  requestedUrls.clear()
  
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.removeItem(URL_TRACKING_KEY)
  }

  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      await caches.delete(CACHE_NAME)
    } catch (e) {
      console.debug('Failed to clear cache:', e)
    }
  }
}

/**
 * Get cache statistics
 */
const getCacheStats = async () => {
  const cache = await getCache()
  if (!cache) {
    return {
      cacheApiAvailable: false,
      trackedUrls: requestedUrls.size,
    }
  }

  try {
    const keys = await cache.keys()
    return {
      cacheApiAvailable: true,
      cachedImages: keys.length,
      trackedUrls: requestedUrls.size,
    }
  } catch (e) {
    return {
      cacheApiAvailable: true,
      cachedImages: 0,
      trackedUrls: requestedUrls.size,
      error: e.message,
    }
  }
}

export {
  cacheImage,
  cacheImages,
  getCachedImageUrl,
  preloadCommonIcons,
  clearCache,
  getCacheStats,
  isImageCached,
}
